import { config } from "./config";

export interface Plan {
  key: string;
  name: string;
  capability: string;
  version: number;
  priceAgorot: number;
  episodesPerPeriod: number;
  reelsPerEpisode: number;
  maxEpisodeMinutes: number;
}

export interface Me {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  fullName: string | null;
  phone: string | null;
  marketingOptIn: boolean;
  profileComplete: boolean;
  requestedPlan: string | null;
  credits: number;
  /** Ever given episodes, so the desktop app may be downloaded. Absent from older API versions. */
  activated?: boolean;
  termsVersion: string;
  termsAccepted: boolean;
}

export interface PlanSummary {
  planVersionId: number;
  key: string;
  name: string;
  priceAgorot: number;
  episodesPerPeriod: number;
}

export interface BillingView {
  /** Whether paid plans can be bought online yet. */
  available: boolean;
  subscription: {
    status: "active" | "past_due" | "cancelled" | "ended";
    plan: PlanSummary;
    nextPlan: PlanSummary | null;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    renews: boolean;
    card: { last4: string | null; expiry: string | null };
    recurringReady: boolean;
  } | null;
  credits: { plan: number; extra: number; total: number };
  payments: Array<{ date: string; kind: "first" | "renewal" | "upgrade"; amountAgorot: number; planName: string; documentNumber: string | null; documentUrl: string | null }>;
}

export interface PlanChange {
  effective: "now" | "next_charge";
  chargedAgorot: number;
  addedCredits: number;
}

export interface DesktopDownload {
  version: string;
  /** Signed link to the installer; works for a few minutes. */
  url: string;
  sizeBytes: number;
  minimumSystemVersion: string;
  expiresAt: string;
}

export interface ProfileInput {
  fullName: string;
  phone: string;
  marketingOptIn: boolean;
}

/** An error answer from the API: HTTP status, error code and, for validation, the field. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly field?: string,
  ) {
    super(`API ${status} ${code}`);
    this.name = "ApiError";
  }
}

async function call<T>(path: string, options: { method?: string; token?: string; body?: unknown } = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.token) {
    headers.Authorization = `Bearer ${options.token}`;
  }
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  let response: Response;
  try {
    response = await fetch(config.apiUrl + path, {
      method: options.method ?? "GET",
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
  } catch {
    throw new ApiError(0, "network");
  }
  if (response.status === 204) {
    return undefined as T;
  }
  const payload = (await response.json().catch(() => ({}))) as { error?: string; field?: string };
  if (!response.ok) {
    throw new ApiError(response.status, payload.error ?? "unknown", payload.field);
  }
  return payload as T;
}

export const api = {
  catalog: () => call<{ plans: Plan[] }>("/v1/catalog").then((result) => result.plans),
  me: (token: string) => call<Me>("/v1/me", { token }),
  saveProfile: (token: string, profile: ProfileInput) =>
    call<void>("/v1/me/profile", { method: "PUT", token, body: profile }),
  acceptTerms: (token: string, version: string) =>
    call<void>("/v1/me/terms", { method: "POST", token, body: { version } }),
  requestPlan: (token: string, planKey: string) =>
    call<void>("/v1/me/plan-request", { method: "POST", token, body: { planKey } }),
  billing: (token: string) => call<BillingView>("/v1/billing/subscription", { token }),
  /** Opens Cardcom's hosted page for the first month; returns where to send the browser. */
  checkout: (token: string, input: { planKey: string; invoiceName?: string; invoiceTaxId?: string }) =>
    call<{ checkoutId: string; url: string }>("/v1/billing/checkout", { method: "POST", token, body: input }),
  checkoutStatus: (token: string, checkoutId: string) =>
    call<{ status: "open" | "paid" | "failed"; purpose: string }>(`/v1/billing/checkouts/${encodeURIComponent(checkoutId)}`, { token }),
  changePlan: (token: string, planKey: string) =>
    call<PlanChange & BillingView>("/v1/billing/subscription/plan", { method: "POST", token, body: { planKey } }),
  cancelSubscription: (token: string) => call<BillingView>("/v1/billing/subscription/cancel", { method: "POST", token }),
  resumeSubscription: (token: string) => call<BillingView>("/v1/billing/subscription/resume", { method: "POST", token }),
  updateCard: (token: string) => call<{ checkoutId: string; url: string }>("/v1/billing/card", { method: "POST", token }),
  /** A short-lived link to the latest Mac installer, for activated accounts. */
  desktopDownload: (token: string) => call<DesktopDownload>("/v1/desktop/download", { method: "POST", token }),
  /** A one-time code that signs the desktop app in (see desktop.ts). */
  desktopHandoff: (token: string, challenge: string) =>
    call<{ code: string; expiresInSeconds: number }>("/v1/desktop/handoff", { method: "POST", token, body: { challenge } }),
};
